/**
 * The public site's footer, shared by both public shells.
 *
 * /docs used to end on a single copyright line, so a reader who finished a
 * guide had nowhere to go but the back button. It ends the same way as every
 * other public page now, which is also the only way the two shells cannot
 * drift: there is one copy of the columns.
 *
 * The home page's closing band — slogan and the two CTAs — is passed in rather
 * than built in, because that is marketing and a documentation page is not the
 * place for it.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useI18n, useLocalizedPath } from '@/lib/i18n';
import { Logo } from '@/components/ui/Logo';

export const REPO = 'https://github.com/kizuna-ai-lab/sokuji';
export const SUPPORT = 'support@kizuna.ai';

/** The footer's four columns. `to` is a route; `href` leaves the site. */
const FOOTER = [
  {
    head: 'footer.product',
    items: [
      { key: 'nav.scenarios', to: '/scenarios' },
      { key: 'nav.features', to: '/#features' },
      { key: 'nav.localAi', to: '/local-ai' },
      { key: 'footer.byok', to: '/providers' },
    ],
  },
  {
    head: 'footer.install',
    items: [
      { label: 'Linux', to: '/docs/install/linux' },
      { label: 'Windows', to: '/docs/install/windows' },
      { label: 'macOS', to: '/docs/install/macos' },
      { key: 'footer.extension', to: '/download#extension' },
    ],
  },
  {
    head: 'footer.docsShortHead',
    items: [
      { key: 'footer.gettingStarted', to: '/docs' },
      { key: 'footer.meetingSites', to: '/docs/supported-sites' },
      { key: 'footer.providerSetup', to: '/docs/ai-providers' },
      { key: 'footer.subtitleMode', to: '/docs/features/subtitle-mode' },
    ],
  },
  {
    head: 'footer.open',
    items: [
      { label: 'GitHub', href: REPO },
      { key: 'nav.privacy', to: '/privacy' },
      { key: 'footer.terms', to: '/docs/terms' },
      { label: SUPPORT, href: `mailto:${SUPPORT}` },
    ],
  },
] as const;

interface SiteFooterProps {
  /** Rendered above the columns, on the same dark ground. */
  children?: ReactNode;
}

export function SiteFooter({ children }: SiteFooterProps) {
  const { t } = useI18n();
  const lp = useLocalizedPath();

  return (
    <div className="landing-layout__end">
      <div className="landing-layout__end-inner">
        {children}

        <footer className="landing-layout__footer">
          <div className="landing-layout__brand">
            <Link to={lp('/')} className="landing-layout__logo landing-layout__logo--footer">
              <Logo size={22} />
              <span>Sokuji</span>
            </Link>
            <p>{t('footer.tagline')}</p>
          </div>

          {FOOTER.map((col) => (
            <div className="landing-layout__col site-label" key={col.head}>
              <span className="landing-layout__col-head">
                {col.head === 'footer.docsShortHead' ? t('nav.docsShort') : t(col.head)}
              </span>
              {col.items.map((item) => {
                const label = 'label' in item ? item.label : t(item.key);
                return 'href' in item ? (
                  <a
                    key={label}
                    href={item.href}
                    target={item.href.startsWith('mailto:') ? undefined : '_blank'}
                    rel="noopener noreferrer"
                  >
                    {label}
                  </a>
                ) : (
                  <Link key={label} to={lp(item.to)}>
                    {label}
                  </Link>
                );
              })}
            </div>
          ))}
        </footer>
      </div>
    </div>
  );
}
